<script>
/**
 * @component Main
 * @description Layout principal con sidebar colapsable, topbar y contenido.
 *              Maneja transiciones de ancho y hamburguesa para mobile.
 * @example <Main />
 */
import SidebarNav from "./layout/SidebarNav.vue";
import SupportForm from "@/components/profile/SupportForm.vue";
// UC-REG-098 — Widget flotante del bot de soporte. Visible solo si el
// tenant tiene `support.assistant` activo y el rol del user != support.
// El componente decide internamente si se monta (this.visible).
import SupportChatWidget from "@/components/support/SupportChatWidget.vue";
import authService from "@/services/auth";
import { capitalize } from "@/services/helper";
import { mapGetters } from "vuex";
import { landingUrl, isMonitoringEnabled, theme } from "@/services/config";

const LS_KEY = "sidebar-collapsed";

/**
 * @description Mapeo `?section=<key>` (query param de SettingsDashboard) →
 *              título a mostrar en el header global cuando se está
 *              navegando una sub-sección de settings.
 *              Agregar acá cada sección que queramos que reemplace
 *              "Configuración" en el header global.
 */
const SETTINGS_SECTION_TITLES = {
  "whatsapp-audit": "Auditoría WhatsApp",
  "whatsapp-config": "Configuración WhatsApp",
  "whatsapp-notifications-config": "Notificaciones WhatsApp",
  "ai-config": "Configuración IA",
  "prescription-log": "Log de recetas",
};

const ROUTE_TITLES = {
  "dashboard-alerts": "Seguimientos",
  patient: "Pacientes",
  treatment: "Protocolos seguimiento",
  "appointment-calendar": "Agenda",
  "appointment-agendas": "Agendas",
  "appointment-schedule": "Atenciones",
  "appointment-relocation": "Citas a reprogramar",
  validation: "Validaciones",
  "validation-history": "Historial de validaciones",
  prescription: "Prescripciones",
  "prescription-queue": "Bandeja de recetas",
  "prescription-history": "Historial de recetas",
  "orders-crm": "CRM de órdenes",
  administration: "Programas de salud",
  "patient-dashboard": "Pacientes",
  "patient-appointments": "Mis turnos",
  "patient-credential": "Mi credencial",
  "patient-monitoring": "Mi seguimiento",
  "users-list": "Usuarios",
  settings: "Configuración",
  "settings-massive": "Carga Masiva",
  contracts: "Contratos",
  profile: "Perfil",
  members: "Afiliados",
  providers: "Prestadores",
  "providers-create": "Nuevo Prestador",
  "providers-edit": "Editar Prestador",
  claims: "Cuenta Médica",
  "service-record-list": "Cuenta Médica",
  "service-record-detail": "Cuenta Médica",
  "liquidation-list": "Cuenta Médica",
  // UC-REG-214 — faltaban las rutas del flujo B (submissions) en el mapa,
  //   por eso el título "Cuenta Médica" desaparecía al entrar a Liquidaciones.
  "liquidation-submission-list": "Cuenta Médica",
  "liquidation-submission-new": "Cuenta Médica",
  "liquidation-submission-detail": "Cuenta Médica",
  "provider-summary": "Cuenta Médica",
  plans: "Planes",
  "plan-detail": "Detalle del Plan",
  "doctor-services": "Prestaciones del Doctor",
  "payments-list": "Pagos",
  authorization: "Autorizaciones",
  "authorization-new": "Nueva Autorización",
  "authorization-detail": "Detalle de Autorización",
  "audit-list": "Auditoría",
  "audit-detail": "Detalle de Auditoría",
  pharmacies: "Farmacias",
  "prescription-purchase": "Comprar medicamento",
  "urgency-queue": "Urgencias",
};

export default {
  name: "Main",

  components: {
    SidebarNav,
    SupportForm,
    SupportChatWidget,
  },

  data() {
    return {
      sidebarCollapsed: localStorage.getItem(LS_KEY) === "true",
      windowWidth: window.innerWidth,
      moreMenuOpen: false,
      /** @type {boolean} Visibilidad del menú de perfil */
      showProfileMenu: false,
      /** @type {boolean} Visibilidad del modal de soporte */
      isSupportModalOpen: false,
      /** @type {boolean} Si los pagos están habilitados (theme dash) */
      isPaymentEnabled: theme === "dash",
      /**
       * @type {boolean} True si la app se está renderizando dentro del WebView
       * de la app mobile. El WebView setea localStorage.inAppWebView=true antes
       * de cargar el dashboard. Cuando es true, se oculta el sidebar y la topbar
       * para que el contenido se vea embebido en la app nativa sin chrome web.
       * Sólo aplica en mobile — en browser normal queda como siempre.
       */
      isInAppWebView: localStorage.getItem("inAppWebView") === "true",
    };
  },

  computed: {
    ...mapGetters(["isPatient", "isAdmin", "role"]),

    /** @returns {string} URL del landing para sacar turno */
    landingUrl() {
      return landingUrl;
    },

    /** @returns {boolean} Si el módulo de monitoreo está habilitado */
    isMonitoringEnabled() {
      return isMonitoringEnabled;
    },

    /** @returns {boolean} Si se muestra la barra inferior de navegación (paciente + mobile) */
    showBottomNav() {
      return this.isPatient && this.isMobile;
    },

    /** @returns {string} Iniciales del usuario para el avatar */
    userInitials() {
      const u = this.$store.state.user;
      const first = (u?.firstName || "")[0] || "";
      const last = (u?.lastName || "")[0] || "";
      return (first + last).toUpperCase() || "?";
    },

    /** @returns {string} Nombre completo truncado */
    userName() {
      const u = this.$store.state.user;
      const name = (u?.firstName || "") + " " + (u?.lastName || "");
      const trimmed = name.trim();
      return trimmed.length > 23 ? `${trimmed.slice(0, 23)}...` : trimmed;
    },

    /** @returns {string} Etiqueta legible del rol */
    userRoleLabel() {
      const map = {
        admin: "Administrador",
        doctor: "Médico",
        patient: "Paciente",
        assistant: "Asistente",
        manager: "Manager",
        admissionist: "Admisionista",
        secretary: "Secretaria",
        "health-admin": "Admin. Salud",
      };
      return map[this.role] || this.role || "";
    },

    /**
     * @description Título de la página actual basado en la ruta.
     *              Soporta override por sub-sección via `?section=` query
     *              param para vistas que renderizan sub-páginas inline (ej.
     *              SettingsDashboard con sub-secciones como Auditoría
     *              WhatsApp, AI Config, etc.). Así el header global cambia
     *              al entrar a la sub-sección, en vez de quedar con el
     *              título del módulo padre.
     * @returns {string}
     */
    pageTitle() {
      const name = this.$route.name || "";
      if (name === "home") {
        const user = this.$store.state.user;
        if (user && user.firstName) {
          return `Hola, ${capitalize(user.firstName)}`;
        }
        return "Inicio";
      }
      // Override por sub-sección de settings — agregar acá cada sección que
      // tenga vista propia y queramos que el header global la refleje.
      if (
        name === "settings" &&
        this.$route.query &&
        this.$route.query.section
      ) {
        const sectionTitle = SETTINGS_SECTION_TITLES[this.$route.query.section];
        if (sectionTitle) return sectionTitle;
      }
      return ROUTE_TITLES[name] || "";
    },

    isPrintRoute() {
      return this.$route.path === "/print";
    },

    /**
     * @description Si estamos en viewport mobile
     * @returns {boolean}
     */
    isMobile() {
      return this.windowWidth < 768;
    },
  },

  mounted() {
    this._onResize = () => {
      this.windowWidth = window.innerWidth;
    };
    window.addEventListener("resize", this._onResize);
  },

  beforeDestroy() {
    window.removeEventListener("resize", this._onResize);
  },

  methods: {
    /** @description Cierra sesión y redirige al login */
    signOut() {
      authService.clearToken();
      this.$router.push({ name: "login" });
    },
    /** @description Navega al perfil y cierra el menú */
    profile() {
      this.$router.push({ name: "profile" });
      this.showProfileMenu = false;
    },
    /** @description Navega a pagos y cierra el menú */
    showPayments() {
      this.$router.push({ name: "payments-list" });
      this.showProfileMenu = false;
    },
    /** @description Abre el modal de soporte */
    showSupportModal() {
      this.showProfileMenu = false;
      this.isSupportModalOpen = true;
    },
    /** @description Cierra el modal de soporte */
    closeSupportModal() {
      this.isSupportModalOpen = false;
    },
    /** @description Cierra el menú de perfil */
    hideProfileMenu() {
      this.showProfileMenu = false;
    },

    /**
     * @description Recibe el evento de colapso del sidebar
     * @param {boolean} collapsed - Estado colapsado
     */
    onSidebarCollapse(collapsed) {
      this.sidebarCollapsed = collapsed;
    },

    /**
     * @description Abre el sidebar en mobile
     */
    openMobileSidebar() {
      if (this.$refs.sidebar) {
        this.$refs.sidebar.toggleCollapse();
        this.moreMenuOpen = this.$refs.sidebar.mobileOpen;
      }
    },
  },
};
</script>

<template lang="pug">
.app-layout(:class="{ 'is-in-app-webview': isInAppWebView }")
  sidebar-nav(v-if="!isPrintRoute && !isInAppWebView" ref="sidebar" @collapse="onSidebarCollapse")
  .app-layout__content(
    :class="{ 'app-layout__content--full': isPrintRoute || isInAppWebView, 'app-layout__content--collapsed': sidebarCollapsed && !isMobile, 'app-layout__content--mobile': isMobile, 'app-layout__content--has-bottom-nav': showBottomNav && !isInAppWebView }"
  )
    .app-topbar(v-if="!isPrintRoute && !isInAppWebView")
      .app-topbar__left
        button.app-topbar__hamburger(v-if="isMobile && !isPatient" @click="openMobileSidebar")
          micon(name="reorder")
        h1.app-topbar__title {{ pageTitle }}
      .app-topbar__right
        nav.user-dropdown(v-click-outside="hideProfileMenu")
          button.user-trigger(@click="showProfileMenu = !showProfileMenu")
            .user-trigger__avatar {{ userInitials }}
            .user-trigger__info
              .user-trigger__name {{ userName }}
              .user-trigger__role {{ userRoleLabel }}
            micon.user-trigger__chevron(:class="{ 'is-open': showProfileMenu }" name="expand_more")
          .user-menu(v-show="showProfileMenu")
            a.user-menu__item(@click.prevent="profile")
              micon(name="person")
              span Mi perfil
            a.user-menu__item(v-if="isPaymentEnabled && isAdmin" @click.prevent="showPayments")
              micon(name="payments")
              span Pagos
            a.user-menu__item(@click.prevent="showSupportModal")
              micon(name="message")
              span Soporte
            .user-menu__divider
            a.user-menu__item.user-menu__item--danger(@click.prevent="signOut")
              micon(name="lock")
              span Cerrar sesión
    support-form(:dialogFormVisible="isSupportModalOpen" @close="closeSupportModal")
    main
      router-view
    footer

    //- Bottom navigation for patients on mobile.
    //- Cuando la app se renderiza dentro del WebView de la app mobile nativa,
    //- la navegación inferior la maneja la propia app — ocultamos esta para
    //- evitar duplicación.
    nav.bottom-nav(v-if="showBottomNav && !isInAppWebView")
      router-link.bottom-nav__item(
        :to="{ name: 'home' }"
        exact
        :class="{ 'is-active': $route.name === 'home' }"
      )
        micon(name="house")
        span Inicio
      router-link.bottom-nav__item(
        :to="{ name: 'patient-me' }"
        :class="{ 'is-active': $route.name === 'patient-me' }"
      )
        micon(name="assignment")
        span Historia
      router-link.bottom-nav__item(
        v-if="isMonitoringEnabled"
        :to="{ name: 'patient-monitoring' }"
        :class="{ 'is-active': $route.name === 'patient-monitoring' }"
      )
        micon(name="favorite")
        span Seguimiento
      router-link.bottom-nav__item(
        :to="{ name: 'patient-credential' }"
        :class="{ 'is-active': $route.name === 'patient-credential' }"
      )
        micon(name="id_card")
        span Credencial
      .bottom-nav__item(@click="openMobileSidebar" :class="{ 'is-active': moreMenuOpen }")
        micon(name="menu")
        span Más

    //- UC-REG-098 — Widget bot de soporte. Se monta condicional (chequeo
    //- interno de feature flag + rol). Posicionado fixed sobre todo.
    support-chat-widget
</template>

<style lang="scss" scoped>
.app-layout {
  display: flex;
  min-height: 100vh;

  &__content {
    margin-left: 260px;
    flex: 1;
    /* UC-REG-052 (fix DEFINITIVO verificado en vivo): este nodo es el flex
       child que envuelve TODO el contenido principal (sidebar fuera). Sin
       min-width:0 explícito, su `min-width:auto` default = min-content del
       contenido interno. Cuando una indicación tiene texto largo (caso
       típico: evolución clínica con descripción de varias líneas, lista de
       laboratorios), el contenido empuja el width de este flex child y
       toda la página se ensancha más allá del viewport. Verificado con
       devtools en vivo: con min-width:0 el offsetWidth pasa de 493px a
       462px (= viewport). Soluciona el bug raíz, hace innecesarios los
       fix downstream previos (overflow-x:hidden en main / .ph-container,
       min-width:0 en .container-boxes .left) que quedan como defensivos. */
    min-width: 0;
    min-height: 100vh;
    display: flex;
    flex-direction: column;
    transition: margin-left 0.3s ease;

    &--collapsed {
      margin-left: 64px;
    }

    &--full {
      margin-left: 0;
    }

    &--mobile {
      margin-left: 0;
    }

    &--has-bottom-nav {
      padding-bottom: 64px;
    }
  }
}

.app-topbar {
  height: 48px;
  background: #fff;
  border-bottom: 1px solid $border-color;
  display: flex;
  align-items: center;
  justify-content: space-between;
  padding: 0 24px;
  position: sticky;
  top: 0;
  z-index: 999;
  flex-shrink: 0;

  @media (max-width: 767px) {
    padding: 0 16px;
  }

  &__left {
    display: flex;
    align-items: center;
    gap: 10px;
  }

  &__right {
    display: flex;
    align-items: center;

    .user-dropdown {
      position: relative;
    }
  }

  &__hamburger {
    background: none;
    border: 1px solid $border-color;
    border-radius: 6px;
    padding: 6px;
    cursor: pointer;
    display: flex;
    align-items: center;
    justify-content: center;

    .micon {
      fill: $text-secondary-new;
      width: 20px;
      height: 20px;
    }

    &:hover {
      background: #f1f5f9;
    }
  }

  &__title {
    font-size: 16px;
    font-weight: 700;
    color: $navy;
    margin: 0;
  }
}

main {
  padding: 24px 32px;
  flex: 1;

  @media (max-width: 767px) {
    padding: 16px;
  }
}

/* ─── Bottom Nav (patient mobile) ─── */
.bottom-nav {
  position: fixed;
  bottom: 0;
  left: 0;
  right: 0;
  height: 60px;
  background: #fff;
  border-top: 1px solid $border-color;
  display: flex;
  align-items: stretch;
  z-index: 1001;
  box-shadow: 0 -2px 8px rgba(0, 0, 0, 0.06);

  &__item {
    flex: 1;
    display: flex;
    flex-direction: column;
    align-items: center;
    justify-content: center;
    gap: 2px;
    text-decoration: none;
    color: #94a3b8;
    font-size: 10px;
    font-weight: 600;
    transition: color 0.15s ease;

    .micon {
      width: 22px;
      height: 22px;
      fill: #94a3b8;
      transition: fill 0.15s ease;
    }

    &.is-active {
      color: $navy;
      .micon {
        fill: $navy;
      }
    }

    &:active {
      background: #f8fafc;
    }
  }
}
</style>

<style lang="scss">
/**
 * Overrides globales para el modo embebido en el WebView de la app mobile.
 * No scoped porque el contenido viene de <router-view> y los selectores
 * tienen que alcanzar a sus descendientes (PatientDashboard, etc.).
 * Solo aplican cuando .app-layout tiene la clase .is-in-app-webview,
 * que se activa cuando el WebView mobile setea localStorage.inAppWebView=true.
 * En browser web normal estos estilos quedan inertes.
 */
.app-layout.is-in-app-webview {
  // Stack vertical en lugar de columnas
  .container-boxes {
    flex-direction: column;
    gap: 10px;
  }

  // Padding reducido y sin desbordamiento horizontal
  .whiteBox,
  .whiteBox.left,
  .whiteBox.right {
    width: 100%;
    max-width: 100%;
    padding: 12px 14px;
    overflow-x: hidden;
    box-sizing: border-box;
  }

  // Texto largo (emails, números, descripciones) que rompa para no cortarse
  .primary-text,
  .secondary-text,
  p,
  span {
    overflow-wrap: break-word;
    word-break: break-word;
  }

  // Botones multilínea y limitados al ancho disponible
  button,
  .button-blue-new,
  .button-teal-new {
    max-width: 100%;
    white-space: normal;
    word-break: break-word;
    min-width: 0;
  }

  // El content principal sin márgenes de chrome de desktop
  .app-layout__content {
    padding: 0;
    margin: 0;
  }

  // Las cards de cualquier acordeón/panel que respeten el ancho
  .el-collapse-item__content,
  .el-card,
  .el-card__body {
    padding: 12px;
  }

  // Ocultar el botón "+ Cargar resultado" del header del acordeón
  // Laboratorios — es redundante con los botones grandes del footer
  // ("Cargar resultado de laboratorio" / "Cargar resultado de imagen")
  // que ya están más abajo. "Ver resultados" se mantiene porque es el
  // único acceso a esa funcionalidad.
  .ph-acc-action-btn--primary {
    display: none;
  }

  // ─── Tabla LabVitalsTable como card layout en mobile ─────────────
  // Por default es un grid de 7 columnas (522px+ ancho fijo) que en
  // un viewport mobile (~360px) se desborda completamente. Acá la
  // convertimos a un layout de "card por indicador" con la info
  // apilada en líneas. Solo aplica bajo .is-in-app-webview, en web
  // queda la tabla original sin tocar.
  .lvt-header {
    display: none; // En card layout, el header de columnas no aplica
  }

  .lvt-row {
    display: grid;
    grid-template-columns: 1fr auto auto;
    grid-template-areas:
      "date  date  chevron"
      "name  name  name"
      "value unit  ."
      "ref   ref   ref"
      "trend trend trend";
    gap: 6px 8px;
    padding: 12px;
    align-items: center;
  }

  .lvt-date {
    grid-area: date;
    font-size: 11px;
    color: #94a3b8;
  }

  .lvt-name {
    grid-area: name;
    font-weight: 600;
    font-size: 14px;
    color: #1e293b;
    white-space: normal; // anular el nowrap default para que wrappee si hace falta
    overflow: visible;
    text-overflow: clip;
  }

  .lvt-value {
    grid-area: value;
    font-size: 20px;
    font-weight: 600;
  }

  .lvt-unit {
    grid-area: unit;
    font-size: 12px;
    color: #94a3b8;
    align-self: end;
    padding-bottom: 4px;
  }

  .lvt-ref {
    grid-area: ref;
    width: 100%;
  }

  .lvt-trend {
    grid-area: trend;
    display: flex;
    flex-direction: row;
    align-items: center;
    gap: 8px;
    width: 100%;
  }

  .lvt-chevron {
    grid-area: chevron;
    font-size: 16px;
    color: #94a3b8;
    align-self: start;
  }

  // Las cards individuales con borde para separarlas visualmente
  .lvt-row-wrap {
    margin-bottom: 8px;
  }
}
</style>
